import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getTenantSettings } from '../services/api';

interface TimezoneContextType {
  timezone: string;
  setTimezone: (tz: string) => void;
  heartbeatTimeout: number;
  setHeartbeatTimeout: (mins: number) => void;
  formatTime: (isoString: string | Date) => string;
  loading: boolean;
}

const TimezoneContext = createContext<TimezoneContextType>({
  timezone: 'Africa/Lagos',
  setTimezone: () => {},
  heartbeatTimeout: 30,
  setHeartbeatTimeout: () => {},
  formatTime: () => '',
  loading: true,
});

export function TimezoneProvider({ children }: { children: React.ReactNode }) {
  const [timezone, setTimezone] = useState('Africa/Lagos');
  const [heartbeatTimeout, setHeartbeatTimeout] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTenantSettings()
      .then((data) => {
        if (data.timezone) setTimezone(data.timezone);
        if (data.heartbeat_timeout_minutes !== undefined) setHeartbeatTimeout(data.heartbeat_timeout_minutes);
      })
      .catch(() => {
        // fallback to default
      })
      .finally(() => setLoading(false));
  }, []);

  const formatTime = useCallback(
    (isoString: string | Date) => {
      try {
        const d = typeof isoString === 'string' ? new Date(isoString) : isoString;
        return d.toLocaleString('en-GB', {
          dateStyle: 'medium',
          timeStyle: 'medium',
          timeZone: timezone,
        });
      } catch {
        return String(isoString);
      }
    },
    [timezone]
  );

  return (
    <TimezoneContext.Provider value={{ timezone, setTimezone, heartbeatTimeout, setHeartbeatTimeout, formatTime, loading }}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  return useContext(TimezoneContext);
}
